import { defineComponent as _defineComponent } from 'vue'
import { useDebounce } from '@vueuse/core'
import { computed, ref, Ref, watch } from 'vue'

import { tripModule, waypointModule } from '@/store'
import { generatedGUID } from '@/utils/utility-manager'
import { LocationAutocompleteResult } from '@/types/trip'
import { useContext } from '@/composables/context'
import { LocationDataSource, WaypointDataSource } from '@/utils/enums'
import { useQuery } from '@/composables/vue-query'

export interface LocationSearchProps {
  mode?: 'onboarding' | 'waypoint' | 'nerbyPlace'
  screenSource?: 'accommodation' | 'default'
  value?: LocationAutocompleteResult
  token?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LocationSearch',
  props: {
    mode: { default: 'waypoint' },
    screenSource: { default: 'default' },
    value: { default: undefined },
    token: { default: generatedGUID() }
  },
  emits: ['input'],
  setup(__props: any, { expose, emit }) {

const props = __props as { mode: 'onboarding' | 'waypoint' | 'nerbyPlace', screenSource: 'accommodation' | 'default', value: LocationAutocompleteResult, token: string };

const locationSearchQueryKeys = {
  all: () => ['locationSearch'] as const,
  search: (debounceQuery: Readonly<Ref<any>>) =>
    [...locationSearchQueryKeys.all(), 'search', debounceQuery] as const,
}

const { $axios } = useContext()



expose({
  /**
   * Fetches the details of the currently selected autocomplete result.
   * This is only necessary if the location data source is `GOOGLE`.
   * For `MAPBOX`, all the required properties are already present in the
   * `location` object and are returned directly.
   */
  fetchDetails: async () => {
    if (
      !props.value?.dataSource ||
      !props.value?.location.placeId ||
      !(props.value.dataSource in WaypointDataSource)
    ) {
      return
    }

    if (props.value.location.dataSource === LocationDataSource.MAPBOX) {
      return props.value.location
    } else if (props.value.location.dataSource === LocationDataSource.GOOGLE) {
      const dataSource = WaypointDataSource[props.value.dataSource]
      const {
        data: {
          data: { location },
        },
      } = await $axios.get(`/api/meta/locations/search/details`, {
        params: {
          ds: dataSource,
          place_id: props.value.location.placeId,
          token: props.token,
        },
      })
      return location
    }
  },
})

const query = ref<string | null>(null)
const debouncedQuery = useDebounce(query, 250)

/**
 * This is used to prevent an intial search request to our api, when the user
 * does not interacted with the input field yet and a valid location is passed
 * as prop.
 */
const isInitialInputMade = ref(false)

const stopInitialInputWatch = watch(query, (newQuery) => {
  if (!location.value || newQuery !== location.value.title) {
    isInitialInputMade.value = true
    stopInitialInputWatch()
  }
})

const queryEnabled = computed(
  () =>
    isInitialInputMade.value &&
    debouncedQuery.value !== null &&
    debouncedQuery.value.length > 2,
)

const location = computed({
  get() {
    return props.value
  },
  set(value) {
    emit('input', value)
  },
})

async function locationAutocomplete(): Promise<LocationAutocompleteResult[]> {
  const query = debouncedQuery.value
  if (!query) {
    return []
  }
  if (props.mode === 'onboarding') {
    const result = await tripModule.fetchCreateTripLocationSearch({
      sessionToken: props.token,
      query,
    })
    return result?.external ?? []
  } else if (props.mode === 'waypoint') {
    const data = await waypointModule.search({
      searchTerm: query,
      screenSource: props.screenSource,
    })
    return data.predictions
  } else {
    return []
  }
}

/**
 * Helper variable to allow changing the selected value from the parent
 * component.
 *
 * If the value is set by the parent component, it is possible that this
 * may not be in the list of fetched locations, so we need to add it to the
 * list.
 */
const locationItems = computed(() => {
  const list = items.value ?? []
  if (location.value && !list.find((item) => item.id === location.value?.id)) {
    list.push(location.value)
  }
  return list
})

const { data: items, isFetching } = useQuery({
  queryKey: locationSearchQueryKeys.search(debouncedQuery),
  queryFn: locationAutocomplete,
  placeholderData: () => (location.value ? [location.value] : []),
  enabled: queryEnabled,
  keepPreviousData: true,
  refetchOnWindowFocus: false,
})

return { __sfc: true,locationSearchQueryKeys, $axios, props, emit, query, debouncedQuery, isInitialInputMade, stopInitialInputWatch, queryEnabled, location, locationAutocomplete, locationItems, items, isFetching }
}

})